import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Globe, QrCode, ArrowLeftRight, Building2, Smartphone } from 'lucide-react';

const features = [
  {
    icon: Globe,
    title: 'Cross-border payments',
    description: 'Send money to 180+ countries with competitive exchange rates and low fees. Payments arrive quickly and reliably.',
    slug: 'cross-border',
  },
  {
    icon: ArrowLeftRight,
    title: 'SWIFT / SEPA transfers',
    description: 'Execute international wire transfers using standard banking rails. Full compliance with global transfer protocols.',
    slug: 'swift-sepa',
  },
  {
    icon: QrCode,
    title: 'QR code payments',
    description: 'Scan and pay at millions of merchants worldwide. Works seamlessly with local QR payment networks across Asia, Europe, and beyond.',
    slug: 'qr-payments',
  },
  {
    icon: Building2,
    title: 'Business payouts',
    description: 'Streamline vendor and contractor payments globally. Batch transfers, scheduled payouts, and detailed reporting.',
    slug: 'business-payouts',
  },
  {
    icon: Smartphone,
    title: 'Multi-platform access',
    description: 'Manage your wallet from any device — web, iOS, or Android. Consistent experience with full-feature parity.',
    slug: 'multi-platform',
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export const Features = () => {
  return (
    <section id="features" className="py-16 sm:py-20" style={{ background: '#F7F7FB' }} data-testid="features-section">
      <div className="container-main">
        <div className="text-center mb-12">
          <p className="font-mono-accent text-xs font-medium uppercase tracking-[0.18em] text-[#7C3AED] mb-3">
            Capabilities
          </p>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0B0B10] mb-4">
            One wallet, endless possibilities
          </h2>
          <p className="text-base text-[#64647A] max-w-lg mx-auto">
            Everything you need to manage global payments from a single, secure platform.
          </p>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className={index === 0 ? 'md:col-span-2 lg:col-span-1' : ''}
            >
              <Card
                data-testid={`feature-card-${feature.slug}`}
                className="bg-white border border-[#E7E7EF] rounded-2xl shadow-sm card-hover h-full"
              >
                <CardContent className="p-6 sm:p-7">
                  <div className="h-10 w-10 rounded-xl bg-[#F5F3FF] text-[#7C3AED] flex items-center justify-center mb-4">
                    <feature.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0B0B10] mb-2">{feature.title}</h3>
                  <p className="text-sm text-[#64647A] leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
